import { useMemo } from "react";
import { Link, useLocation, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import classNames from "classnames";

import Icon, { COLORS, ICONS } from "../../general-ui/icon";
import { useTypedSelector } from "../../../store/reducers/use-typed-selector";
import { setPanelOpen } from '../../../store/actions/admin/event-panel';
import { getHomepageDefaultLanguage } from "../../../utils/get-language";
import { ParamsProps } from "../../live-event/live-event";
import './sidebar-nav.scss';
import allLanguages from '../../../utils/translate-langs.json';
import { PATHNAMES } from "../../../utils/admin-routing-utils";
import { OptionalComponent } from "../../../utils/optional-component";
import { FeatureFlagsEnum } from "../../../types/working-model";


export default function EventNav(): JSX.Element {

	const { workingEvent, eventUuid, workingSession, featureFlags } = useTypedSelector(state => ({
		workingEvent: state.CreateEventReducer.workingEvent,
		eventUuid: state.CreateEventReducer.workingEvent?.uuid,
		workingSession: state.CreateSessionReducer.workingSession,
		featureFlags: state.FeatureFlagsReducer.featureFlags
	}));

	const dispatch = useDispatch();

	const location = useLocation();
	const { pathname } = location;

	const params = useParams() as ParamsProps;
	const defaultLanguage = workingEvent?.sessions ? getHomepageDefaultLanguage() : "en";

	const matched = allLanguages.find(lang => lang.code === params.language);
	const language = matched?.code ?? defaultLanguage;
	const displayEmailsFeature = featureFlags[FeatureFlagsEnum.emails_and_audiences];

	// conditional routing for theme to keep the editor window in current view
	const themeNav: string = useMemo(() => {
		return pathname.includes("landing") ? "landing" : pathname.includes("session") && workingSession ? `session/${workingSession.uuid}/${workingSession.default_language}` : "";
	}, [pathname, workingSession]);

	return (
		<div className="event-nav">
			{eventUuid && <nav>
				<Link
					className={classNames({ active: pathname === PATHNAMES.Event.LandingLink(eventUuid, language) })}
					to={PATHNAMES.Event.LandingLink(eventUuid, language)}
					onClick={() => dispatch(setPanelOpen(true))}>
					<div className="icon"><Icon name={ICONS.DESKTOP_SLIM} size={24} color={pathname === PATHNAMES.Event.LandingLink(eventUuid, language) ? COLORS.WHITE : COLORS.MED_LIGHT_GRAY} /></div>
					Landing
				</Link>
				<Link
					// have to use includes here so will show active when re-routed to tab
					className={classNames({ active: pathname.includes(PATHNAMES.Event.RegistrationLink(eventUuid, language)) })}
					to={PATHNAMES.Event.RegistrationLink(eventUuid, language)}
					onClick={() => dispatch(setPanelOpen(true))}>
					<div className="icon"><Icon name={ICONS.REGISTRATION} size={24} color={pathname.includes(PATHNAMES.Event.RegistrationLink(eventUuid, language)) ? COLORS.WHITE : COLORS.MED_LIGHT_GRAY} /></div>
					Registration
				</Link>
				<Link
					className={classNames({ active: pathname === PATHNAMES.Event.EventLink(eventUuid, language) })}
					to={PATHNAMES.Event.EventLink(eventUuid, language)}
					onClick={() => dispatch(setPanelOpen(true))}>
					<div className="icon"><Icon name={ICONS.EVENTS_NAV} size={24} color={pathname === PATHNAMES.Event.EventLink(eventUuid, language) ? COLORS.WHITE : COLORS.MED_LIGHT_GRAY} /></div>
					Event
				</Link>
				<Link
					className={classNames({ active: pathname.includes("social") })}
					to={PATHNAMES.Event.SocialLink(eventUuid, language)}
					onClick={() => dispatch(setPanelOpen(true))}>
					<div className="icon"><Icon name={ICONS.PEOPLE} size={24} color={pathname === PATHNAMES.Event.SocialLink(eventUuid, language) ? COLORS.WHITE : COLORS.MED_LIGHT_GRAY} /></div>
					Social
				</Link>
				{/* FUTURE DEV: remove this optional component wrapper when we deploy the emails feature */}
				<OptionalComponent display={displayEmailsFeature || workingEvent?.registration_on}>
					<Link
						className={classNames({ active: pathname.includes("emails") })}
						to={PATHNAMES.Event.EmailListLink(eventUuid, language)}
						onClick={() => dispatch(setPanelOpen(true))}>
						<div className="icon"><Icon name={ICONS.EMAIL_FILLED} size={24} color={[PATHNAMES.Event.EmailListLink(eventUuid, language), PATHNAMES.Event.EmailDetailsLink(eventUuid, language, '', 'settings')].includes(pathname) ? COLORS.WHITE : COLORS.MED_LIGHT_GRAY} /></div>
						Emails
					</Link>
				</OptionalComponent>
				<Link
					// also using includes here as there are a few different theme paths
					className={classNames({ active: pathname.includes("theme") })}
					to={PATHNAMES.Event.ThemeLink(eventUuid, language, themeNav)}
					onClick={() => dispatch(setPanelOpen(true))}>
					<div className="icon"><Icon name={ICONS.THEME_PALETTE} size={24} color={pathname.includes("theme") ? COLORS.WHITE : COLORS.MED_LIGHT_GRAY} /></div>
					Theme
				</Link>

				{/* FUTURE DEV - feature is not being implemented for MVP but will be used in the future, refactor to use links, currentNav & NAV_LABELS are deprecated */}
				{/* <div>
                    <button className={classNames({ selected: currentNav === NAV_LABELS.Comments })} onClick={() => handleClick(NAV_LABELS.Comments)}>
                        <Icon name={ICONS.TAB_CHAT} size={24} color={currentNav === NAV_LABELS.Comments ? COLORS.WHITE : COLORS.GRAY} />
                    </button>
                    {capitalize(NAV_LABELS.Comments)}
                </div> */}
				{/* FUTURE DEV - feature is not being implemented for MVP but will be used in the future, refactor to use links, currentNav & NAV_LABELS are deprecated */}
				{/* <div>
                    <button className={classNames({ selected: currentNav === NAV_LABELS.Versions })} onClick={() => handleClick(NAV_LABELS.Versions)}>
                        <Icon name={ICONS.VERSIONS} size={24} color={currentNav === NAV_LABELS.Versions ? COLORS.WHITE : COLORS.GRAY} />
                    </button>
                    {capitalize(NAV_LABELS.Versions)}
                </div> */}
				{/* FUTURE DEV - no settings currently but saving for possible future use */}
				{/* <Link
					className={classNames({ active: pathname === EVENTPATHS[NAV_LABELS.Settings] })}
					to={EVENTPATHS[NAV_LABELS.Settings]}
					onClick={() => dispatch(updateCurrentNav(NAV_LABELS.Settings))}>
					<Icon name={ICONS.SETTINGS_TRANSPARENT} size={24} color={pathname === EVENTPATHS[NAV_LABELS.Settings] ? COLORS.WHITE : COLORS.GRAY} />
					{capitalize(NAV_LABELS.Settings)}
				</Link> */}
			</nav>}
		</div>
	);
}